import React, { useMemo, useState } from 'react';
import { View, Text, TextInput, TouchableOpacity, ScrollView, StyleSheet, Dimensions } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { useTheme } from '../contexts/ThemeContext';
import Footer from '../components/Footer';
import { formatCurrency, formatNumber } from '../utils/formatters';

const { width } = Dimensions.get('window');

export default function StockAverageCalculatorScreen() {
  const { colors } = useTheme();
  const navigation = useNavigation();

  const [rows, setRows] = useState([
    { qty: '100', price: '100' },
    { qty: '50', price: '80' },
  ]);

  const num = (v, d = 0) => {
    const n = parseFloat(String(v).replace(/,/g, ''));
    if (isNaN(n)) return d;
    return n;
  };

  const totals = useMemo(() => {
    const parsed = rows.map(r => ({ qty: Math.max(0, num(r.qty, 0)), price: Math.max(0, num(r.price, 0)) }));
    const totalQty = parsed.reduce((s, r) => s + r.qty, 0);
    const totalCost = parsed.reduce((s, r) => s + r.qty * r.price, 0);
    const avgPrice = totalQty > 0 ? (totalCost / totalQty) : 0;
    return { totalQty, totalCost, avgPrice };
  }, [rows]);

  const updateRow = (index, field, value) => {
    setRows(prev => prev.map((r, i) => (i === index ? { ...r, [field]: value } : r)));
  };

  const addRow = () => {
    setRows(prev => [...prev, { qty: '0', price: '0' }]);
  };

  const removeRow = (index) => {
    setRows(prev => prev.filter((_, i) => i !== index));
  };

  const resetAll = () => {
    setRows([{ qty: '100', price: '100' }, { qty: '50', price: '80' }]);
  };

  const currency = new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 });

  return (
    <ScrollView style={styles.container} contentContainerStyle={styles.content}>
      <View style={styles.heroSection}>
        <View style={styles.heroContent}>
          <Text style={styles.heroTitle}>Stock Average Calculator</Text>
          <Text style={styles.heroSubtitle}>Compute average buy price across multiple purchases</Text>
        </View>
      </View>

      <View style={styles.calculatorContainer}>
        <View style={styles.calculatorRow}>
          <View style={styles.leftPanel}>
            <View style={styles.card}>
              <Text style={styles.sectionTitle}>Purchase Legs</Text>
              {rows.map((row, idx) => (
                <View key={idx} style={{ marginBottom: 8 }}>
                  <View style={styles.row}>
                    <View style={[styles.inputGroup, styles.flexItem]}>
                      <Text style={styles.label}>Quantity</Text>
                      <TextInput value={row.qty} onChangeText={(v) => updateRow(idx, 'qty', v)} style={styles.input} keyboardType="numeric" placeholder="e.g. 100" />
                    </View>
                    <View style={[styles.inputGroup, styles.flexItem]}>
                      <Text style={styles.label}>Price (₹)</Text>
                      <TextInput value={row.price} onChangeText={(v) => updateRow(idx, 'price', v)} style={styles.input} keyboardType="numeric" placeholder="e.g. 100" />
                    </View>
                  </View>
                  {rows.length > 1 && (
                    <TouchableOpacity onPress={() => removeRow(idx)} style={styles.removeBtn}><Text style={styles.removeBtnText}>Remove</Text></TouchableOpacity>
                  )}
                </View>
              ))}
              <View style={{ flexDirection: 'row', gap: 8, marginTop: 8 }}>
                <TouchableOpacity style={styles.buttonSecondary} onPress={addRow}><Text style={styles.buttonTextAlt}>Add Row</Text></TouchableOpacity>
              </View>
            </View>
          </View>

          <View style={styles.rightPanel}>
            <View style={styles.card}>
              <View style={styles.panelHeader}>
                <Text style={styles.panelTitle}>Average Summary</Text>
                <Text style={styles.panelSubtitle}>Weighted average of all purchases</Text>
              </View>
              <View style={styles.summaryRow}><Text style={styles.summaryLabel}>Total Quantity</Text><Text style={styles.summaryValue}>{formatNumber(totals.totalQty)}</Text></View>
              <View style={styles.summaryRow}><Text style={styles.summaryLabel}>Average Price</Text><Text style={styles.summaryValue}>{currency.format(totals.avgPrice)}</Text></View>
            </View>

            <View style={styles.card}>
              <View style={styles.panelHeader}>
                <Text style={styles.panelTitle}>Cost Details</Text>
                <Text style={styles.panelSubtitle}>Aggregate investment</Text>
              </View>
              <View style={styles.summaryRow}><Text style={styles.summaryLabel}>Total Cost</Text><Text style={styles.summaryValue}>{currency.format(totals.totalCost)}</Text></View>
              <View style={{ marginTop: 12, flexDirection: 'row', gap: 8 }}>
                <TouchableOpacity style={styles.buttonPrimary} onPress={resetAll}><Text style={styles.buttonText}>Reset</Text></TouchableOpacity>
              </View>
            </View>
          </View>
        </View>
      </View>

      {renderSEO(navigation)}
      {renderFAQ()}
      <Footer />
    </ScrollView>
  );
}

function renderSEO(navigation) {
  return (
    <View style={styles.seoSection}>
      <Text style={styles.seoTitle}>About Stock Averaging</Text>
      <Text style={styles.seoText}>
        Averaging adjusts your cost basis across multiple purchase prices. This tool computes weighted average price, total quantity, and total cost so you can quickly see break-even and evaluate additional buys.
      </Text>

      <Text style={styles.seoSubtitle}>Formula</Text>
      <Text style={styles.seoText}>
        Weighted Average Price = Σ(qty × price) ÷ Σ(qty). Total cost = Σ(qty × price). Ensure inputs reflect post-split or bonus-adjusted quantities and prices.
      </Text>

      <Text style={styles.seoSubtitle}>Charges and taxes</Text>
      <Text style={styles.seoText}>
        Brokerage, taxes, and fees are not included here. Use the Brokerage Calculator to estimate charges and incorporate them into your effective cost if needed.
      </Text>

      <View style={styles.linksRow}>
        <TouchableOpacity style={styles.linkButton} onPress={() => navigation.navigate('BrokerageCalculator')}>
          <Text style={styles.linkText}>Brokerage Calculator</Text>
        </TouchableOpacity>
        <TouchableOpacity style={styles.linkButton} onPress={() => navigation.navigate('MarginCalculator')}>
          <Text style={styles.linkText}>Margin Calculator</Text>
        </TouchableOpacity>
        <TouchableOpacity style={styles.linkButton} onPress={() => navigation.navigate('XIRRCalculator')}>
          <Text style={styles.linkText}>XIRR Calculator</Text>
        </TouchableOpacity>
      </View>

      <Text style={styles.disclaimer}>
        Results are indicative. Trading involves risk. Verify corporate actions and charges when computing actual cost basis.
      </Text>
    </View>
  );
}

function renderFAQ() {
  return (
    <View style={styles.faqContainer}>
      <Text style={styles.faqTitle}>Frequently Asked Questions</Text>

      <View style={styles.faqItem}>
        <Text style={styles.faqQuestion}>Does the tool include brokerage and taxes?</Text>
        <Text style={styles.faqAnswer}>No. It focuses on quantity and price. Use the Brokerage Calculator to estimate additional costs.</Text>
      </View>

      <View style={styles.faqItem}>
        <Text style={styles.faqQuestion}>How do I handle stock splits or bonuses?</Text>
        <Text style={styles.faqAnswer}>Adjust quantities and prices to post-corporate-action values before inputting to maintain accurate averages.</Text>
      </View>

      <View style={styles.faqItem}>
        <Text style={styles.faqQuestion}>What happens after partial sales?</Text>
        <Text style={styles.faqAnswer}>Your remaining position’s average price may change. Track buys and sells to recompute cost basis correctly.</Text>
      </View>

      <View style={styles.faqItem}>
        <Text style={styles.faqQuestion}>Is averaging always beneficial?</Text>
        <Text style={styles.faqAnswer}>Not necessarily. Averaging lowers cost basis but can increase exposure to a declining asset. Assess fundamentals and risk.</Text>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#f8fafc' },
  content: { paddingBottom: 24 },
  heroSection: { backgroundColor: '#4F46E5', paddingVertical: 18, paddingHorizontal: 16 },
  heroContent: { maxWidth: 1200, alignSelf: 'center' },
  heroTitle: { fontSize: 22, fontWeight: '800', color: '#ffffff', marginBottom: 4 },
  heroSubtitle: { fontSize: 14, color: '#e0e7ff' },
  calculatorContainer: { width: '100%', alignSelf: 'stretch', paddingHorizontal: 16, paddingVertical: 12 },
  calculatorRow: { flexDirection: 'row', gap: 24 },
  leftPanel: { flex: 0.6 },
  rightPanel: { flex: 0.4 },
  card: { backgroundColor: '#ffffff', borderRadius: 12, padding: 16, marginTop: 16, marginHorizontal: 16, borderWidth: 1, borderColor: '#e5e7eb' },
  sectionTitle: { fontSize: 18, fontWeight: '700', color: '#0f172a', marginBottom: 12, marginHorizontal: 16 },
  row: { flexDirection: 'row', alignItems: 'center', gap: 12 },
  inputGroup: { marginVertical: 6 },
  input: { backgroundColor: '#f1f5f9', borderWidth: 1, borderColor: '#e5e7eb', borderRadius: 8, paddingHorizontal: 12, paddingVertical: 10 },
  label: { fontSize: 13, color: '#334155', marginBottom: 6 },
  flexItem: { flex: 1 },
  panelHeader: { alignItems: 'center', marginBottom: 10, paddingBottom: 8, borderBottomWidth: 1, borderBottomColor: '#e5e7eb' },
  panelTitle: { fontSize: 18, fontWeight: '600', color: '#0f172a' },
  panelSubtitle: { fontSize: 12, color: '#64748b', marginTop: 2 },
  summaryRow: { flexDirection: 'row', justifyContent: 'space-between', paddingVertical: 6 },
  summaryLabel: { color: '#334155' },
  summaryValue: { color: '#0f172a', fontWeight: '600' },
  buttonPrimary: { backgroundColor: '#4F46E5', paddingVertical: 10, paddingHorizontal: 14, borderRadius: 8 },
  buttonSecondary: { backgroundColor: '#EEF2FF', borderWidth: 1, borderColor: '#c7d2fe', paddingVertical: 10, paddingHorizontal: 14, borderRadius: 8 },
  buttonText: { color: '#ffffff', fontWeight: '600' },
  buttonTextAlt: { color: '#1e293b', fontWeight: '600' },
  removeBtn: { alignSelf: 'flex-start', marginTop: 6, backgroundColor: '#fee2e2', borderColor: '#fecaca', borderWidth: 1, borderRadius: 6, paddingVertical: 6, paddingHorizontal: 10 },
  removeBtnText: { color: '#991b1b', fontWeight: '600' },
  seoSection: { backgroundColor: '#ffffff', borderRadius: 12, padding: 16, marginTop: 16, borderWidth: 1, borderColor: '#e5e7eb', marginHorizontal: 16 },
  seoTitle: { fontSize: 18, fontWeight: '700', color: '#0f172a', marginBottom: 8 },
  seoSubtitle: { fontSize: 16, fontWeight: '600', color: '#0f172a', marginTop: 12, marginBottom: 6 },
  seoText: { color: '#334155', lineHeight: 20 },
  linksRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginTop: 10 },
  linkButton: { backgroundColor: '#EEF2FF', borderColor: '#c7d2fe', borderWidth: 1, borderRadius: 8, paddingVertical: 8, paddingHorizontal: 12 },
  linkText: { color: '#1e293b', fontWeight: '600' },
  disclaimer: { color: '#64748b', fontSize: 12, marginTop: 10 },
  faqContainer: { backgroundColor: '#ffffff', borderRadius: 12, padding: 16, marginTop: 16, borderWidth: 1, borderColor: '#e5e7eb', marginHorizontal: 16 },
  faqTitle: { fontSize: 18, fontWeight: '700', color: '#0f172a', marginBottom: 8 },
  faqItem: { marginBottom: 10 },
  faqQuestion: { fontSize: 15, fontWeight: '600', color: '#0f172a' },
  faqAnswer: { color: '#334155', marginTop: 4, lineHeight: 20 },
});